import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toastDuration, type ToastKind } from '../lib/toast';

type Toast = {
  id: number;
  kind: ToastKind;
  message: string;
};

type ToastContextValue = {
  showToast: (kind: ToastKind, message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const nextId = useRef(0);

  const showToast = useCallback((kind: ToastKind, message: string) => {
    nextId.current += 1;
    setToast({ id: nextId.current, kind, message });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), toastDuration(toast.kind));
    return () => window.clearTimeout(timeout);
  }, [toast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        aria-live={toast?.kind === 'error' ? 'assertive' : 'polite'}
        aria-atomic="true"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex justify-center px-4"
      >
        {toast && (
          <button
            key={toast.id}
            type="button"
            onClick={() => setToast(null)}
            title="Dismiss notification"
            className="pointer-events-auto flex max-w-xl cursor-pointer items-center gap-2.5 rounded-xl border border-line bg-white px-5 py-3 text-left text-sm text-ink shadow-[0_2px_10px_-6px_rgba(11,15,13,0.2)]"
          >
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${
                toast.kind === 'success' ? 'bg-success' : 'bg-error'
              }`}
            />
            {toast.message}
          </button>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider.');
  return context;
}
